// Odvodí paid/paid_at pri vytváraní výdavku (Phase 0.5 — odlev cashflow).
// Default podľa typu dokladu: bloček/iný doklad = zaplatený hneď (paid_at = deň
// zápisu, lebo uhradený na mieste kartou/hotovosťou); faktúra od dodávateľa =
// zatiaľ záväzok (nezaplatená). input.paid má prednosť keď ho UI pošle explicitne.
export function deriveExpensePaid(input = {}) {
  const isSupplierInvoice = input.document_type === 'faktura';
  const paid = input.paid != null ? !!input.paid : !isSupplierInvoice;
  const paid_at = paid ? (input.paid_at || input.entry_date || null) : null;
  return { paid, paid_at };
}

// record — univerzálny záznam (M0 plní len module='expense').
import { db, newCommonFields, putSetting } from './db';
import { isValidIsoDate, todayIso } from './dateUtils';
import { learnVendor } from './vendors';
import { upsertCounterparty } from './counterparties';
import { formatInvoiceNumberV2, vsFromNumber, invoiceCounterKey, formatNumberForKind, DOC_KINDS } from './invoiceNumber';
import { getSellerProfile, emptySellerProfile } from './sellerProfile';

// Suma DPH z brutto + sadzby (brutto = základ + DPH). 1190 centov @ 19% → 190 c.
// Brutto * rate / (100 + rate), zaokrúhlené na celé centy. Bez floatu v DB.
// Vracia null ak nemáme brutto alebo platnú sadzbu (>0).
export function computeVatAmountCents(amountCents, vatRate) {
  if (!(amountCents > 0)) return null;
  const rate = Number(vatRate);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return Math.round((amountCents * rate) / (100 + rate));
}

// Brutto-first rozklad výdavku: `grossCents` (suma celkom) je AUTORITATÍVNE, a keď
// sú základ aj DPH definované, MUSIA sa naň sčítať (subtotal + vat === gross).
// Predtým sa základ ukladal surovo z inputu a DPH nezávisle z brutto+sadzby →
// user-editovaný základ dával subtotal+vat != gross (nekonzistencia #6kl, Codex).
// Priorita zdroja rozkladu:
//   1) autoritatívna DPH (OCR, presná aj multi-rate) → základ = brutto - DPH
//   2) user zadaný základ (v rozsahu [0, brutto]) → DPH = brutto - základ
//   3) inak DPH zo sadzby; základ ostáva null (nemáme spoľahlivý základ) — zachová
//      súčasné správanie non-payer defaultu (vat/subtotal null) bez zápisu odhadu.
// Nekonzistentný vstup (DPH/základ mimo [0, brutto]) sa NEfabrikuje — degraduje na
// ďalšiu prioritu (zápornú DPH nikdy neuložíme).
// KONTRAKT: keď user edituje základ/brutto/sadzbu, volajúci MUSÍ poslať
// vatAmountCents=null (OCR DPH už neplatí) — inak by tu OCR vetva prebila user základ.
// AddExpenseV2 to zaručuje cez `ocrVatStillValid` (zmena základu → OCR DPH neplatná).
export function reconcileExpenseAmounts({ grossCents, vatAmountCents, subtotalCents, vatRate }) {
  const inRange = (v) => v != null && Number.isFinite(v) && v >= 0 && v <= grossCents;
  if (inRange(vatAmountCents)) {
    return { vat_amount_cents: vatAmountCents, subtotal_cents: grossCents - vatAmountCents };
  }
  if (inRange(subtotalCents)) {
    return { vat_amount_cents: grossCents - subtotalCents, subtotal_cents: subtotalCents };
  }
  return { vat_amount_cents: computeVatAmountCents(grossCents, vatRate), subtotal_cents: null };
}

/**
 * Vyhľadá možného duplikátneho výdavku medzi existujúcimi záznamami.
 *
 * @param {object} input - { entry_date, amount_cents, counterparty_ico?, counterparty_name? }
 * @param {Array} existingExpenses - pole existujúcich expense záznamov
 * @returns {object|null} nájdený existujúci záznam alebo null
 */
export function findDuplicateExpenseCandidate(input, existingExpenses) {
  if (!input || !existingExpenses || !Array.isArray(existingExpenses)) return null;
  if (!(input.amount_cents > 0) || !input.entry_date) return null;

  // Identita dodávateľa na vstupe
  const inputIco = input.counterparty_ico?.trim() || null;
  const inputNameNormalized = input.counterparty_name?.trim().toLowerCase() || null;

  // Ak nemáme ani IČO ani meno, nemôžeme porovnávať
  if (!inputIco && !inputNameNormalized) return null;

  for (const expense of existingExpenses) {
    if (expense.deleted_at != null) continue; // zmazané ignorujeme

    // Suma a dátum musia presne sedieť
    if (expense.amount_cents !== input.amount_cents) continue;
    if (expense.entry_date !== input.entry_date) continue;

    // Identita dodávateľa existujúceho záznamu.
    const expIco = expense.counterparty_ico?.trim() || null;
    const expNameNormalized = expense.counterparty_name?.trim().toLowerCase() || null;

    let match = false;
    // Ak OBAJA majú IČO, je autoritatívne — zhoda/rozdiel IČO rozhoduje, meno sa
    // v tomto prípade neberie do úvahy (iné IČO = iná firma, aj keby náhodou malo
    // rovnaké meno).
    if (inputIco && expIco) {
      match = (inputIco === expIco);
    } else {
      // Chýba IČO na jednej (alebo oboch) stranách → fallback na normalizované meno.
      //
      // ZÁMERNÉ ROZHODNUTIE (Opus, 2026-08-06): cross-family review (Gemini, autopilot
      // kolo 3) navrhol toto považovať za bug — „prítomnosť IČO na jednej strane a jeho
      // absencia na druhej znamená inú firmu". Zvážené a ZAMIETNUTÉ: reálny scenár, pre
      // ktorý je celá táto funkcia postavená, je TEN ISTÝ fyzický doklad nahraný 2×
      // rôznou cestou (raz cez QR/eKasa lookup ktorý IČO dotiahne, raz cez foto-OCR ktoré
      // IČO nevytiahne) — presne to je "duplicita 2 cestami" scenár z Fable verdiktu.
      // Vynútenie symetrie na "IČO musí byť na oboch stranách inak nikdy nezhoda" by
      // TICHO ROZBILO presne tento hlavný use-case (regresia vlastného pozitívneho
      // kontrakt-testu nižšie: "chýbajúce IČO, ale rovnaký normalizovaný názov… → nájde").
      // Cena falošného positívu je nízka (Daniel: appka len VAROVUJE, neblokuje) — cena
      // falošného negatívu (skutočná duplicita prejde ticho) je presne pôvodný nájdený bug.
      // Preferujeme vyšší recall. Kalibrované explicitným testom v oboch smeroch (rovnaké
      // meno + IČO na oboch stranách musí zablokovať zhodu, ak sa IČO líšia — vyššie).
      match = (inputNameNormalized && expNameNormalized && inputNameNormalized === expNameNormalized);
    }

    if (match) {
      return expense; // prvá zhoda
    }
  }

  return null;
}

// Vytvor výdavok (AC9). input = {
//   entry_date, amount_cents, counterparty_name, counterparty_ico,
//   category_id, category_src, label, attachment_ref,
//   vat_rate?, doc_number?, ocr_raw?  (voliteľné — z OCR; fallback null)
//   // Rozšírené polia (V1 parity, všetky voliteľné):
//   counterparty_dic?, counterparty_icdph?, vat_amount_cents?, subtotal_cents?,
//   due_date?, variable_symbol?, iban?, document_type?, paid_via?, currency?,
//   extraction_source?, extraction_confidence?, items?[],
//   client_id?  (voliteľné — FK na db.counterparties.id; na ktorého klienta sa
//                výdavok ráta pre ziskovosť klienta. Default null = bez klienta.)
// }
// Vracia uložený record. Po uložení spustí learning loop (AC15).
export async function createExpense(input) {
  if (!input?.entry_date) throw new Error('entry_date je povinné');
  // Brutto musí byť kladné A bezpečné celé číslo centov — inak by brutto-first
  // rozklad (gross - vat) stratil presnosť nad MAX_SAFE_INTEGER a subtotal+vat != gross
  // (Codex cross-family). Reálne sumy sú ďaleko pod hranicou; guard chráni invariant.
  if (!(input?.amount_cents > 0) || !Number.isSafeInteger(input.amount_cents)) {
    throw new Error('amount_cents musí byť kladné celé číslo centov');
  }

  // Kontrola na možného duplikáta – pred zápisom do DB
  let duplicateCandidate = null;
  try {
    const existing = await listExpenses();
    duplicateCandidate = findDuplicateExpenseCandidate(input, existing);
  } catch (e) {
    // Zlyhanie dedup kontroly nikdy nesmie zablokovať vytvorenie výdavku
    console.warn('[v2] duplicate check failed, proceeding', e);
  }

  // Brutto-first rozklad: brutto (amount_cents) je autoritatívne; základ + DPH sa naň
  // musia sčítať. reconcileExpenseAmounts drží konzistenciu aj pri user-editovanom
  // základe (predtým subtotal+vat != gross, #6kl).
  const vatRate = input.vat_rate ?? null;
  const { vat_amount_cents: vatAmountCents, subtotal_cents: subtotalCents } =
    reconcileExpenseAmounts({
      grossCents: input.amount_cents,
      vatAmountCents: input.vat_amount_cents ?? null,
      subtotalCents: input.subtotal_cents ?? null,
      vatRate,
    });

  // Zaplatené? (Phase 0.5 — odlev cashflow). Default podľa typu dokladu.
  const { paid, paid_at: paidAt } = deriveExpensePaid(input);

  const record = {
    ...newCommonFields(),
    module: 'expense',
    entry_date: input.entry_date,
    paid,
    paid_at: paidAt,
    label: input.label || null,
    counterparty_name: input.counterparty_name || null,
    counterparty_ico: input.counterparty_ico || null,
    counterparty_dic: input.counterparty_dic || null,
    counterparty_icdph: input.counterparty_icdph || null,
    doc_number: input.doc_number ?? null,
    amount_cents: input.amount_cents,
    subtotal_cents: subtotalCents,
    currency: input.currency || 'EUR',
    vat_amount_cents: vatAmountCents,
    vat_rate: vatRate,
    due_date: input.due_date || null,
    variable_symbol: input.variable_symbol || null,
    iban: input.iban || null,
    document_type: input.document_type || null,
    paid_via: input.paid_via || null,
    items: Array.isArray(input.items) ? input.items : [],
    category_id: input.category_id || null,
    category_src: input.category_id ? (input.category_src || 'user') : null,
    client_id: input.client_id || null,   // FK na db.counterparties (ziskovosť klienta, P4)
    status: 'confirmed',
    source: 'manual',
    attachment_ref: input.attachment_ref || null,
    custom: {},
    ocr_raw: input.ocr_raw ?? null,
    extraction_source: input.extraction_source || null,
    extraction_confidence:
      typeof input.extraction_confidence === 'number' ? input.extraction_confidence : null,
  };

  await db.records.add(record);

  // Po úspešnom zápise pripneme informáciu o duplikáte (iba do návratového objektu, NIE do DB)
  record.duplicateOf = duplicateCandidate
    ? {
        id: duplicateCandidate.id,
        entry_date: duplicateCandidate.entry_date,
        amount_cents: duplicateCandidate.amount_cents,
      }
    : null;

  // Learning loop — zapíš/uprav vendor (AC15).
  if (record.category_id && (record.counterparty_ico || record.counterparty_name)) {
    try {
      await learnVendor({
        ico: record.counterparty_ico,
        name: record.counterparty_name,
        category_id: record.category_id,
      });
    } catch (e) {
      console.warn('[v2] learnVendor failed', e);
    }
  }

  return record;
}

// Zoznam výdavkov (AC16): module='expense', deleted_at null, entry_date zostupne.
export async function listExpenses() {
  const all = await db.records
    .where('module')
    .equals('expense')
    .filter((r) => r.deleted_at == null)
    .toArray();
  // entry_date zostupne; pri zhode novšie created_at hore
  all.sort((a, b) => {
    if (a.entry_date !== b.entry_date) return a.entry_date < b.entry_date ? 1 : -1;
    return a.created_at < b.created_at ? 1 : -1;
  });
  return all;
}

export async function getExpense(id) {
  return await db.records.get(id);
}

// Oprava kategórie výdavku PO uložení (P5 — learning loop, oprava v zozname).
// Prepíše category_id + category_src='user' (ručná oprava) + rev++ + updated_at,
// a zavolá learnVendor → tá istá oprava sa naučí (nabudúce zaradí podľa novej voľby).
// Vracia { record, learnedFrom } — learnedFrom = { ico, name } pre Undo prúžok
// (alebo null ak sa nedalo učiť — chýba dodávateľ aj IČO).
export async function updateExpenseCategory(id, category_id) {
  const rec = await db.records.get(id);
  if (!rec || rec.module !== 'expense') throw new Error('Výdavok neexistuje');
  if (!category_id) throw new Error('category_id je povinné');

  const patch = {
    category_id,
    category_src: 'user',
    updated_at: new Date().toISOString(),
    rev: (rec.rev || 1) + 1,
  };
  await db.records.update(id, patch);

  let learnedFrom = null;
  if (rec.counterparty_ico || rec.counterparty_name) {
    try {
      await learnVendor({
        ico: rec.counterparty_ico,
        name: rec.counterparty_name,
        category_id,
      });
      learnedFrom = { ico: rec.counterparty_ico, name: rec.counterparty_name };
    } catch (e) {
      console.warn('[v2] learnVendor (updateExpenseCategory) failed', e);
    }
  }

  return { record: { ...rec, ...patch }, learnedFrom };
}

// Prepni zaplatené/nezaplatené na výdavku (Phase 0.5 — odlev cashflow). paid=true
// nastaví paid_at (dnes, ak sa nepodá), paid=false ho vyčistí. rev++ + updated_at.
export async function updateExpensePaid(id, paid, paidAt = null) {
  const rec = await db.records.get(id);
  if (!rec || rec.module !== 'expense') throw new Error('Výdavok neexistuje');
  const isPaid = !!paid;
  const patch = {
    paid: isPaid,
    paid_at: isPaid ? (paidAt || todayIso()) : null, // LOKÁLNY dátum, nie UTC (#14)
    updated_at: new Date().toISOString(),
    rev: (rec.rev || 1) + 1,
  };
  await db.records.update(id, patch);
  return { ...rec, ...patch };
}

// Vytvor hotovostný príjem (M1). input = { entry_date, amount_cents, label?, category_id? }
// Rovnaké newCommonFields() ako createExpense — centy, UUID, rev=1, timestamps,
// module='cash_income', status confirmed. DB schéma sa NEMENÍ (existujúca records
// tabuľka + module index).
export async function createCashIncome(input) {
  if (!input?.entry_date) throw new Error('entry_date je povinné');
  if (!(input?.amount_cents > 0)) throw new Error('amount_cents musí byť > 0');

  const record = {
    ...newCommonFields(),
    module: 'cash_income',
    entry_date: input.entry_date,
    label: input.label || null,
    counterparty_name: null,
    counterparty_ico: null,
    counterparty_dic: null,
    counterparty_icdph: null,
    doc_number: null,
    amount_cents: input.amount_cents,
    subtotal_cents: null,
    currency: input.currency || 'EUR',
    vat_amount_cents: null,
    vat_rate: null,
    due_date: null,
    variable_symbol: null,
    iban: null,
    document_type: null,
    paid_via: input.paid_via || 'cash',
    items: [],
    category_id: input.category_id || null,
    category_src: input.category_id ? 'user' : null,
    status: 'confirmed',
    source: 'manual',
    attachment_ref: null,
    custom: {},
    ocr_raw: null,
    extraction_source: null,
    extraction_confidence: null,
  };
  await db.records.add(record);
  return record;
}

// Zoznam hotovostných príjmov (M1): module='cash_income', deleted_at null,
// entry_date zostupne. Analogické k listExpenses().
export async function listCashIncome() {
  const all = await db.records
    .where('module')
    .equals('cash_income')
    .filter((r) => r.deleted_at == null)
    .toArray();
  all.sort((a, b) => {
    if (a.entry_date !== b.entry_date) return a.entry_date < b.entry_date ? 1 : -1;
    return a.created_at < b.created_at ? 1 : -1;
  });
  return all;
}

// ─── Faktúra (vystavovanie) — module='invoice' (spec 009) ─────────────────────
// Faktúra je NETTO-first: DPH sa ráta NAD základ (na rozdiel od createExpense
// ktorý je brutto-first). Preto vlastný výpočet vat_cents, NIE computeVatAmountCents.
//   vat_cents = round(line_base_cents * rate / 100)
// Neplatca DPH (default, Decision 3 freelancer-first): rate 0 → vat_cents 0 →
// amount = subtotal. Model ale UNESIE vat per riadok (pre Wave 1 platcu).
//
// 0 fetch / 0 Supabase / 0 auth / 0 LLM — čistá lokálna Dexie operácia (R4b/#1).

// Rok pre číslovanie faktúry z issue_date. TZ-safe: číta rok zo stringu
// 'YYYY-MM-DD' (NIE new Date(...).getFullYear() — to v západných TZ vráti
// predošlý rok pre 1.1., čo by daňovo posunulo celý číselný rad). Fallback = teraz.
export function deriveInvoiceYear(issueDate, now = new Date()) {
  const m = typeof issueDate === 'string' && issueDate.match(/^(\d{4})-\d{2}-\d{2}/);
  return m ? Number(m[1]) : now.getFullYear();
}

// Vyčisti jeden vstupný riadok na centový tvar s VAT objektom per riadok (D1).
// is_dph_payer=false (default) → rate 0, vat_cents 0.
function buildInvoiceLine(rawItem, isDphPayer, reverseCharge = false) {
  const qty = Number(rawItem?.qty);
  const unitPriceCents = Math.round(Number(rawItem?.unit_price_cents) || 0);
  const lineBaseCents = Math.round((Number.isFinite(qty) ? qty : 0) * unitPriceCents);
  // Sadzba: neplatca → vždy 0. Reverse-charge (prenesenie daňovej povinnosti) →
  // vždy 0 % (dodávateľ neúčtuje DPH, odvádza ju odberateľ). Platca inak → sadzba
  // riadku (default 23). Zdroj SK pravidiel: resources/refs/todue-reverse-charge-invoicing-2026-07.md
  const rate = (isDphPayer && !reverseCharge) ? Number(rawItem?.vat_rate ?? rawItem?.vat?.rate ?? 23) : 0;
  const vatCents = rate > 0 ? Math.round((lineBaseCents * rate) / 100) : 0;
  return {
    desc: rawItem?.desc || '',
    qty: Number.isFinite(qty) ? qty : 0,
    unit: rawItem?.unit || 'ks',
    unit_price_cents: unitPriceCents,
    line_base_cents: lineBaseCents,
    vat: { rate, base_cents: lineBaseCents, vat_cents: vatCents },
  };
}

// Vytvor faktúru (AC1-AC6). input = {
//   issue_date (povinné), tax_date?, due_date?,
//   counterparty: { name (povinné), ico?, dic?, ic_dph?, address? },
//   items: [{ desc, qty>0, unit?, unit_price_cents>0, vat_rate? }],  // aspoň 1 platný
//   is_dph_payer? (default false → neplatca),
//   income_type? (default 'zivnost'), variable_symbol?, seller? (inak settings),
//   _year? (test-only — fixne rok pre číslovanie),
//   _id? (interné — deterministický record id, používa createProformaFromOutbox
//         pre multi-device konvergenciu; bežné volania NEPOSIELAJÚ)
// }
// Vracia uložený record module='invoice' so status 'issued'. Číslo z atomického
// counteru (AC6). Celý zápis (counter increment + record add) beží v JEDNEJ
// Dexie 'rw' transakcii → žiadne duplicitné číslo pri race.
export async function createInvoice(input) {
  if (!input?.issue_date) throw new Error('issue_date je povinné');
  const rawItems = Array.isArray(input?.items) ? input.items : [];
  const validItems = rawItems.filter(
    (it) =>
      it &&
      String(it.desc || '').trim() !== '' &&
      Number(it.qty) > 0 &&
      Math.round(Number(it.unit_price_cents) || 0) > 0
  );
  if (validItems.length === 0) {
    throw new Error('Faktúra potrebuje aspoň 1 položku s popisom a kladnou sumou');
  }

  const isDphPayer = input.is_dph_payer === true; // default false = neplatca (AC4)
  // W2 — prenesenie daňovej povinnosti (reverse charge): dodávateľ neúčtuje DPH.
  const reverseCharge = input.reverse_charge === true;
  // W2 — typ dokladu (vlastný číselný rad). Default 'invoice' (spätne kompatibilné).
  const docKind = DOC_KINDS.includes(input.doc_kind) ? input.doc_kind : 'invoice';
  // W2 — mena wire-through (predtým natvrdo EUR). Default EUR.
  const currency = (input.currency && String(input.currency).trim()) || 'EUR';

  // Riadky s VAT per riadok (AC2). Reverse-charge → všetky sadzby 0 %.
  const items = validItems.map((it) => buildInvoiceLine(it, isDphPayer, reverseCharge));

  // Súčty — všetko int centy (AC3).
  // DPH súčet je PER-KATEGÓRIA (EN 16931 BR-CO-14/BR-CO-17): riadky sa
  // zgrupujú podľa sadzby, základ skupiny sa sčíta a DPH sa zaokrúhli RAZ
  // per skupina — round(Σ base × rate / 100). Per-riadkové vat_cents ostávajú
  // v items len na zobrazenie riadku; súčet faktúry z nich NESČÍTAVAJ (drift
  // ±1 cent per riadok by rozbil e-faktúru aj parity s PDF totálom).
  const subtotalCents = items.reduce((s, it) => s + it.line_base_cents, 0);
  const vatGroups = new Map(); // rate → Σ base_cents
  for (const it of items) {
    const rate = it.vat.rate;
    vatGroups.set(rate, (vatGroups.get(rate) || 0) + it.vat.base_cents);
  }
  let vatTotalCents = 0;
  for (const [rate, baseCents] of vatGroups) {
    if (rate > 0) vatTotalCents += Math.round((baseCents * rate) / 100);
  }
  const amountCents = subtotalCents + vatTotalCents; // k úhrade (BT-115)

  // Snapshot odberateľa (AC5) — D1 tvar + denormalizované polia pre index/zoznam.
  const cp = input.counterparty || {};
  const counterparty = {
    id: cp.id || undefined,
    name: cp.name || '',
    ico: cp.ico || '',
    dic: cp.dic || '',
    ic_dph: cp.ic_dph || '',
    address: cp.address || '',
    // email patrí do snapshotu — eFaktúra (BT-49 buyer electronic address)
    // ho číta z record.counterparty; bez neho by reálna faktúra nikdy
    // neprešla validačným gate-om (Peppol endpoint je povinný).
    email: cp.email || '',
    type: 'customer',
  };

  // Snapshot predajcu — z inputu alebo zo settings (AC5/AC8). Prázdny OK.
  const seller = input.seller
    ? { ...emptySellerProfile(), ...input.seller }
    : await getSellerProfile();

  const issueDate = input.issue_date;
  const taxDate = input.tax_date || issueDate; // BT-72 default = dátum vystavenia
  const dueDate = input.due_date || null;
  const year = input._year || deriveInvoiceYear(issueDate);

  // Atomický counter + add v JEDNEJ transakcii (AC6). Counter key + formát sú
  // per doc kind (W2 seam — dnes stub = jeden rad, Worker B rozdelí na PF/DP).
  const counterKey = invoiceCounterKey(docKind);
  const record = await db.transaction('rw', db.records, db.settings, async (tx) => {
    const counterRow = await tx.settings.get(counterKey);
    const seq = (Number(counterRow?.value) || 0) + 1;
    await putSetting({ key: counterKey, value: seq }, tx.settings);
    const number = formatNumberForKind(docKind, year, seq);
    const variableSymbol = input.variable_symbol || vsFromNumber(number);

    const rec = {
      ...newCommonFields(),
      // _id override (outbox ingest) — deterministický id z outbox_id, aby 2
      // zariadenia spracujúce tú istú entry skonvergovali na 1 record (Drive dedup).
      ...(input._id ? { id: input._id } : {}),
      module: 'invoice',
      entry_date: issueDate, // = issue_date (aby metrics/inPeriod fungovali)
      issue_date: issueDate,
      tax_date: taxDate,
      due_date: dueDate,
      payment_date: null,
      number,
      counterparty,
      counterparty_name: counterparty.name || null,
      counterparty_ico: counterparty.ico || null,
      counterparty_dic: counterparty.dic || null,
      counterparty_icdph: counterparty.ic_dph || null,
      seller,
      income_type: input.income_type || 'zivnost',
      currency, // W2 — mena wire-through (reálny stĺpec, default EUR)
      items,
      subtotal_cents: subtotalCents, // BT-109
      vat_total_cents: vatTotalCents, // BT-110
      amount_cents: amountCents, // k úhrade (BT-115) — kvôli metrics
      vat_amount_cents: vatTotalCents, // existujúce pole, parity
      vat_rate: (isDphPayer && !reverseCharge) ? null : 0, // reverse-charge → 0; multi-sadzba → null
      variable_symbol: variableSymbol,
      iban: seller.iban || null,
      due: dueDate,
      status: 'issued',
      source: 'manual',
      // nepoužité expense polia ostávajú null (parity s expense tvarom)
      label: null,
      doc_number: null,
      document_type: 'invoice',
      paid_via: null,
      category_id: null,
      category_src: null,
      attachment_ref: null,
      ocr_raw: null,
      extraction_source: null,
      extraction_confidence: null,
      // W2 metadata v JSON stĺpci `custom` (records tabuľka má explicitné stĺpce —
      // nové top-level pole by INSERT zhodil, ako records.paid). Mapper číta
      // reverse_charge odtiaľto → e-faktúra VAT kategória 'AE'. doc_kind = typ
      // dokladu (číselný rad). Zdroj: refs/todue-reverse-charge-invoicing-2026-07.md
      custom: {
        reverse_charge: reverseCharge,
        reverse_charge_note: reverseCharge
          ? (input.reverse_charge_note || 'prenesenie daňovej povinnosti')
          : null,
        doc_kind: docKind,
      },
      schema_version: 1,
      // Handshake stav — aktualizovaný pri sendInvoiceHandshake + pollInvoiceStatus.
      handshake: {
        status: 'none',          // 'none' | 'sent' | 'viewed' | 'accepted'
        accept_token: null,      // capability token (32 hex) — owner drží pre polling + re-display linku
        anyone_permission_id: null,  // Drive permissionId pre revoke
        view_url: null,          // Drive webViewLink (anyone viewer)
        viewed_at: null,         // kedy príjemca otvoril náhľad (read-receipt, z Workera)
        accepted_at: null,       // kedy príjemca prijal (z Workera)
        shared_with: null,       // email pre Flow V2 (účtovník), alebo null
      },
    };
    await tx.records.add(rec);
    return rec;
  });

  // Wave 1 — perzistuj odberateľa do tabuľky klientov + naviaž FK id na faktúru.
  // Best-effort (ako learnVendor): zlyhanie nesmie zhodiť vystavenie faktúry.
  // Faktúra naďalej nesie SNAPSHOT odberateľa, plus nový counterparty.id (FK).
  if (record.counterparty?.name) {
    try {
      const cp = await upsertCounterparty(record.counterparty);
      if (cp?.id) {
        record.counterparty.id = cp.id;
        await db.records.update(record.id, { counterparty: record.counterparty });
      }
    } catch (e) {
      console.warn('[v2] upsertCounterparty failed', e);
    }
  }

  return record;
}

// W2 — konverzia proformy na ostrú faktúru. Proforma (PF rad) sa NEmaže —
// ostáva ako doklad, len dostane custom.converted_to (id novej faktúry).
// Nová faktúra vznikne cez createInvoice (doc_kind 'invoice' → reálny fakturačný
// rad 2026NNN), s rovnakým odberateľom/predajcom/položkami/menou; issue_date =
// dnes (nový dátum vystavenia, voliteľný override). reverse_charge + platca DPH
// sa prenášajú z proformy. Guard: už skonvertovaná proforma hodí chybu — nikdy
// nevystavíme druhé ostré číslo z tej istej proformy.
export async function convertProformaToInvoice(proformaId, issueDate = null) {
  const proforma = await db.records.get(proformaId);
  if (!proforma || proforma.module !== 'invoice' || proforma.deleted_at != null) {
    throw new Error('Proforma faktúra neexistuje');
  }
  if (proforma.custom?.doc_kind !== 'proforma') {
    throw new Error('Doklad nie je proforma faktúra — konvertovať sa dá len proforma');
  }
  if (proforma.custom?.converted_to) {
    throw new Error('Proforma už bola konvertovaná na faktúru');
  }

  // Rekonštrukcia vstupných položiek z uložených riadkov (D1 tvar → input tvar).
  const items = (proforma.items || []).map((line) => ({
    desc: line.desc,
    qty: line.qty,
    unit: line.unit,
    unit_price_cents: line.unit_price_cents,
    vat_rate: line.vat?.rate,
  }));

  // Platca DPH sa odvodí z proformy: nenulová DPH na riadku = platca; alebo
  // reverse-charge proforma (sadzby 0, ale vat_rate=0 top-level) — platca tiež
  // (neplatca reverse-charge nevystavuje). vat_rate === null = platca multi-sadzba.
  const reverseCharge = proforma.custom?.reverse_charge === true;
  const isDphPayer =
    reverseCharge ||
    proforma.vat_rate === null ||
    (proforma.items || []).some((l) => (l.vat?.rate || 0) > 0);

  const invoice = await createInvoice({
    doc_kind: 'invoice',
    issue_date: issueDate || todayIso(), // LOKÁLNY dátum, nie UTC (#14)
    due_date: proforma.due_date || null,
    counterparty: { ...proforma.counterparty },
    seller: proforma.seller ? { ...proforma.seller } : undefined,
    items,
    is_dph_payer: isDphPayer,
    reverse_charge: reverseCharge,
    reverse_charge_note: proforma.custom?.reverse_charge_note || undefined,
    currency: proforma.currency || 'EUR',
    income_type: proforma.income_type || 'zivnost',
  });

  // Obojsmerný link: faktúra ← proforma. Faktúra nesie converted_from,
  // proforma converted_to (ten zároveň funguje ako idempotency guard vyššie).
  const invoiceCustom = { ...(invoice.custom || {}), converted_from: proformaId };
  await db.records.update(invoice.id, {
    custom: invoiceCustom,
    updated_at: new Date().toISOString(),
    rev: (invoice.rev || 1) + 1,
  });
  invoice.custom = invoiceCustom;

  await db.records.update(proformaId, {
    custom: { ...(proforma.custom || {}), converted_to: invoice.id },
    updated_at: new Date().toISOString(),
    rev: (proforma.rev || 1) + 1,
  });

  return invoice;
}

// ─── Proforma z outboxu (E3 most #1: workflow engine → SPA) ──────────────────
// Workflow engine appenduje entries do __outbox JSON na Drive; outboxIngest.js
// ich pri sync-u zhmotňuje na proforma records TÝMITO funkciami. source_* polia
// žijú VÝHRADNE v `custom` JSON stĺpci (records má explicitné stĺpce — nové
// top-level pole by INSERT zhodil, ako records.paid).

// Deterministický record id proformy z outbox_id. 2 zariadenia spracujú tú istú
// entry → ten istý id → record-{id}.json na Drive → dedup zlúči na 1 doklad
// (PF číslo counter drift je akceptovateľný, id konverguje). outbox_id je
// stabilný (engine ho zapíše RAZ) → priamy odvod bez hashovania stačí a je
// kolízie-free.
export function proformaIdFromOutboxId(outboxId) {
  return `pf-${String(outboxId)}`;
}

// Nájdi živú proformu podľa zdrojovej outbox entry (idempotency lookup, klon
// findTaskBySource). Vracia record alebo null.
export async function findProformaBySource(outboxId) {
  if (!outboxId) return null;
  const rows = await db.records
    .where('module')
    .equals('invoice')
    .filter(
      (r) =>
        r.deleted_at == null &&
        r.custom?.doc_kind === 'proforma' &&
        r.custom?.source_outbox_id === outboxId
    )
    .toArray();
  return rows[0] || null;
}

// Vytvor proformu z outbox entry. `invoiceInput` = zmapovaný createInvoice input
// (outboxIngest.outboxEntryToProformaInput — mapper žije tam, aby records.js
// neimportoval outboxIngest a nevznikol cyklus).
//
// Idempotency guard PRED create: existujúca živá proforma s rovnakým
// custom.source_outbox_id sa vráti (žiadny duplikát). Deterministický id
// (proformaIdFromOutboxId) navyše chráni multi-device: aj keby lookup minul
// (tombstone / race), druhý INSERT na ten istý id neprebehne — vrátime
// existujúci record podľa id.
export async function createProformaFromOutbox(entry, invoiceInput) {
  const outboxId = entry?.outbox_id;
  if (!outboxId) throw new Error('outbox_id je povinné');

  const existing = await findProformaBySource(outboxId);
  if (existing) return existing;

  const id = proformaIdFromOutboxId(outboxId);
  const byId = await db.records.get(id);
  if (byId) return byId; // tombstone / race — nikdy druhý INSERT na ten istý id

  const proforma = await createInvoice({
    ...invoiceInput,
    doc_kind: 'proforma',
    _id: id,
  });

  // source_* metadata do custom JSON (vzor convertProformaToInvoice post-create).
  const custom = {
    ...(proforma.custom || {}),
    source_outbox_id: outboxId,
    source_run_id: entry.run_id || null,
  };
  await db.records.update(proforma.id, {
    custom,
    updated_at: new Date().toISOString(),
    rev: (proforma.rev || 1) + 1,
  });
  proforma.custom = custom;

  return proforma;
}

// ─── Task (advisor → povinnosť) — module='task' (Fáza B) ─────────────────────
// Advisor odporúčanie sa materializuje na task record; z neho neskôr derivácia
// povinností. source_* polia žijú VÝHRADNE v `custom` JSON stĺpci — records má
// explicitné stĺpce a nové top-level pole by INSERT zhodil (ako records.paid).

// Nájdi živý task podľa zdrojového odporúčania (idempotency lookup).
// Vracia record alebo null.
export async function findTaskBySource(sourceRecommendationId) {
  if (!sourceRecommendationId) return null;
  const tasks = await db.records
    .where('module')
    .equals('task')
    .filter(
      (r) =>
        r.deleted_at == null &&
        r.custom?.source_recommendation_id === sourceRecommendationId
    )
    .toArray();
  return tasks[0] || null;
}

// Vytvor task record z advisor odporúčania. input = {
//   label (povinné), due_date (povinné, valídny ISO), container_id?,
//   source_recommendation_id (povinné pre idempotenciu), source_rule_id?, source_pack_ref?
// }
// Write-side gate: jeden zlý task by zhodil CELÚ deriváciu povinností, preto
// label + due_date validujeme tvrdo (throw). Idempotency: rovnaké
// source_recommendation_id → vráti EXISTUJÚCI task, žiadny duplikát.
export async function createTask({
  label,
  due_date,
  container_id = null,
  source_recommendation_id,
  source_rule_id = null,
  source_pack_ref = null,
} = {}) {
  if (typeof label !== 'string' || label.trim() === '') {
    throw new Error('label je povinný');
  }
  if (!due_date || !isValidIsoDate(due_date)) {
    throw new Error('due_date je povinný valídny ISO dátum (YYYY-MM-DD)');
  }

  // Idempotency guard (vzor convertProformaToInvoice custom.converted_to) —
  // existujúci živý task s rovnakým zdrojom vraciame namiesto duplikátu.
  const existing = await findTaskBySource(source_recommendation_id);
  if (existing) return existing;

  const record = {
    ...newCommonFields(),
    module: 'task',
    entry_date: todayIso(), // ordering v zoznamoch
    due_date,
    label,
    container_id, // reálny stĺpec (idx_records_container_id) — OK top-level
    status: 'confirmed',
    source: 'advisor',
    amount_cents: null,
    // source_* polia — JEDINÉ miesto je custom JSON (top-level by zhodilo INSERT)
    custom: { source_recommendation_id, source_rule_id, source_pack_ref },
  };

  await db.records.add(record);
  return record;
}

// ─── Dokument (auto-file) — module='document' ─────────────────────────────────
// Auto-file pipeline (capture → normalize → resolveDrivePath → uploadToPath)
// materializuje zaradený doklad na record. Klasifikačné metadáta
// (canonical_type/category/confidence) žijú VÝHRADNE v `custom` JSON stĺpci —
// records má explicitné stĺpce a nové top-level pole by INSERT zhodil (ako
// records.paid).

// attachment_ref je scalar TEXT stĺpec (NIE JSON_COLUMNS) — adapter objekt pri
// INSERT-e obranne stringifikuje, ale pri čítaní ho NEparsuje. Defenzívny parse
// pre idempotency lookup (znesie objekt aj string tvar).
function parseAttachmentRef(ref) {
  if (!ref) return null;
  if (typeof ref === 'object') return ref;
  if (typeof ref === 'string') {
    try { return JSON.parse(ref); } catch { return null; }
  }
  return null;
}

// Nájdi živý dokument podľa Drive fileId prílohy (idempotency lookup, vzor
// findTaskBySource). Vracia record alebo null.
async function findDocumentByDriveFileId(driveFileId) {
  if (!driveFileId) return null;
  const rows = await db.records
    .where('module')
    .equals('document')
    .filter(
      (r) =>
        r.deleted_at == null &&
        parseAttachmentRef(r.attachment_ref)?.driveFileId === driveFileId
    )
    .toArray();
  return rows[0] || null;
}

// Nájdi živý dokument podľa source (cross-writer idempotency lookup, vzor
// findDocumentByDriveFileId). Vracia record alebo null. Soft-deleted ignoruje
// (re-file po zmazaní je vedomá akcia, guard ju nesmie blokovať).
async function findDocumentBySource(source) {
  if (!source) return null;
  const rows = await db.records
    .where('module')
    .equals('document')
    .filter((r) => r.deleted_at == null && r.source === source)
    .toArray();
  return rows[0] || null;
}

// ISO dátum ('2026-07-10…') → 4-ciferný rok string '2026', inak ''. Pre L2
// mail-dedup kľúč (mailDedup.mailDedupKey), ktorý rok očakáva ako YYYY.
function docYearOf(isoDate) {
  const m = /^(\d{4})/.exec(typeof isoDate === 'string' ? isoDate : '');
  return m ? m[1] : '';
}

/**
 * Zoznam živých dokumentov (module='document') pre mail-ingest runner
 * (listExistingDocs dep). Dva účely, preto plochý tvar s oboma sadami polí:
 *   1) source-based idempotency guard — runner hľadá doc.source === 'gmail:<id>'
 *      (filing prebehol, ale markMailIdsProcessed zlyhal → bez tohto 2× record);
 *   2) L2 cross-channel dedup kľúč — canonical_type + year + doc_number +
 *      amount_cents (mailDedup.mailDedupKey).
 * canonical_type žije v custom JSON stĺpci; rok odvodíme z issue/entry_date.
 * Read-only, nikdy nehádže do syncu (caller má best-effort try/catch).
 */
export async function listDocumentsForIngest() {
  const rows = await db.records
    .where('module')
    .equals('document')
    .filter((r) => r.deleted_at == null)
    .toArray();
  return rows.map((r) => ({
    source: r.source || null,
    canonical_type: r.custom?.canonical_type ?? null,
    year: docYearOf(r.issue_date || r.entry_date),
    doc_number: r.doc_number ?? null,
    amount_cents: r.amount_cents ?? null,
  }));
}

// Vytvor záznam zaradeného dokladu (auto-file pipeline). FROZEN CONTRACT.
// normalizedDoc = výstup normalizeDocument ({ canonical_type, category, confidence,
//   extracted: { doc_number, issue_date, amount_cents, currency } }),
// drivePathObj = výstup resolveDrivePath ({ dir, filename, path }),
// opts = { driveFileId?, source?, counterparty_name? }.
// entry_date/issue_date z extracted.issue_date, fallback null — doklad bez dátumu
// sa NEzahadzuje (na rozdiel od createExpense throw).
// Idempotency guard PRED create (vzor findProformaBySource): rovnaký
// opts.driveFileId ALEBO rovnaký KANONICKÝ opts.source ('kanál:kľúč', napr.
// gmail:<entryId>) → vráti EXISTUJÚCI record, žiadny duplikát. Source guard
// zatvára cross-writer dieru (batchIngest mohol ten istý doklad zapísať PRVÝ);
// kanálové literály bez ':' ('auto-file', 'upload') NIE sú unikátne → preskočí.
// DB partial unique index (module,source) v schema.js je posledná poistka —
// race medzi guardom a INSERTom sa chytí gracefully (vráti existujúci riadok).
// Vracia uložený record module='document'.
export async function createDocumentRecord(normalizedDoc, drivePathObj, opts = {}) {
  const normalized = normalizedDoc && typeof normalizedDoc === 'object' ? normalizedDoc : {};
  const extracted =
    normalized.extracted && typeof normalized.extracted === 'object' ? normalized.extracted : {};

  const source = opts.source || 'auto-file';
  const canonicalSource =
    typeof source === 'string' && source.includes(':') ? source : null;
  if (canonicalSource) {
    const existing = await findDocumentBySource(canonicalSource);
    if (existing) return existing;
  }

  if (opts.driveFileId) {
    const existing = await findDocumentByDriveFileId(opts.driveFileId);
    if (existing) return existing;
  }

  const record = {
    ...newCommonFields(),
    module: 'document',
    entry_date: extracted.issue_date || null,
    issue_date: extracted.issue_date || null,
    doc_number: extracted.doc_number ?? null,
    amount_cents: extracted.amount_cents ?? null,
    currency: extracted.currency || 'EUR',
    counterparty_name: opts.counterparty_name || null,
    status: 'confirmed',
    source,
    attachment_ref: {
      driveFileId: opts.driveFileId || null,
      path: drivePathObj?.path || null,
    },
    // klasifikačné metadáta — JEDINÉ miesto je custom JSON (top-level by zhodilo INSERT)
    custom: {
      canonical_type: normalized.canonical_type ?? null,
      category: normalized.category ?? null,
      confidence: normalized.confidence ?? null,
      // OCR-vytiahnuté doplnkové polia (max extrakcia). Provenance „z rozpoznávania,
      // môže byť nepresné". Do custom (žiadny DDL), stabilný tvar (null keď chýba).
      // Odtiaľto ich číta advisor / Context Bundle. Sumy/číslo/dátum/mena už top-level;
      // tu platobné, daňové a entitné polia.
      extracted: {
        entity_name: extracted.entity_name ?? null,
        counterparty_ico: extracted.counterparty_ico ?? null,
        iban: extracted.iban ?? null,
        variable_symbol: extracted.variable_symbol ?? null,
        constant_symbol: extracted.constant_symbol ?? null,
        specific_symbol: extracted.specific_symbol ?? null,
        due_date: extracted.due_date ?? null,
        ic_dph: extracted.ic_dph ?? null,
        dic: extracted.dic ?? null,
      },
    },
  };

  try {
    await db.records.add(record);
  } catch (err) {
    // Race: druhý writer zapísal ten istý kanonický source medzi guardom
    // a INSERTom — DB unique index (module,source) to chytil. Vráť existujúci
    // riadok (idempotentná sémantika), nie crash. Iné chyby propagujú.
    if (canonicalSource && /UNIQUE constraint failed/i.test(err?.message || '')) {
      const existing = await findDocumentBySource(canonicalSource);
      if (existing) return existing;
    }
    throw err;
  }
  return record;
}

// Aktualizuj handshake stav faktúry. Volaná po sendInvoiceHandshake alebo pollInvoiceStatus.
// patch = čiastočný objekt handshake (mergujeme s existujúcim, nie prepíšeme celý).
export async function updateInvoiceHandshake(id, handshakePatch) {
  const rec = await db.records.get(id);
  if (!rec || rec.module !== 'invoice') throw new Error('Faktúra neexistuje');
  const existing = rec.handshake || { status: 'none', accept_token: null,
    anyone_permission_id: null, view_url: null, viewed_at: null, accepted_at: null, shared_with: null };
  const patch = {
    handshake: { ...existing, ...handshakePatch },
    updated_at: new Date().toISOString(),
    rev: (rec.rev || 1) + 1,
  };
  await db.records.update(id, patch);
  return { ...rec, ...patch };
}

// Zoznam faktúr (AC7): module='invoice', deleted_at null, issue_date zostupne.
export async function listInvoices() {
  const all = await db.records
    .where('module')
    .equals('invoice')
    .filter((r) => r.deleted_at == null)
    .toArray();
  all.sort((a, b) => {
    const ai = a.issue_date || a.entry_date || '';
    const bi = b.issue_date || b.entry_date || '';
    if (ai !== bi) return ai < bi ? 1 : -1;
    return a.created_at < b.created_at ? 1 : -1;
  });
  return all;
}

export async function getInvoice(id) {
  return await db.records.get(id);
}

// Zmena stavu faktúry (AC19 voliteľné „Označiť ako zaplatené"). status='paid'
// nastaví payment_date (dnes alebo zadaný). Counter sa NEdotýka (rad nemá diery).
export async function updateInvoiceStatus(id, status, paymentDate = null) {
  const rec = await db.records.get(id);
  if (!rec || rec.module !== 'invoice') throw new Error('Faktúra neexistuje');
  const patch = { status, updated_at: new Date().toISOString(), rev: (rec.rev || 1) + 1 };
  if (status === 'paid') {
    patch.payment_date = paymentDate || todayIso(); // LOKÁLNY dátum, nie UTC (#14)
  } else {
    // Návrat na nezaplatené (undo) — vyčisti dátum platby, nech stav sedí.
    patch.payment_date = null;
  }
  await db.records.update(id, patch);
  return { ...rec, ...patch };
}
